import React, { useState, useEffect } from "react";
import { getUsers } from "../../services/user.service";
import { useNavigate, NavigateFunction } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {Chip, FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import IPage from "../../types/page.type";
import IUser from "../../types/user.type";
import {Box, Breadcrumbs, Grid, Typography, Button, ListItem, Checkbox} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import IRole from "../../types/role.type";
import {getRoles} from "../../services/role.service";

const UserList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [usersPage, setUsersPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [keywordFilter, setKeywordFilter] = useState( "");
  const [roleFilter, setRoleFilter] = useState( "-1");
  const [roles, setRoles] = useState<Array<IRole>>();

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<IUser>[] = [
    { 
      field: 'id', 
      headerName: 'ID',  
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60, 
      flex: 1
    },
    {
      field: 'username',
      headerName: 'Nombre de Usuario',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100, 
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'department',
      headerName: 'Sector',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{`${params.row.department==="ADMINISTRATION" ? 'Administracion' : 'Operaciones'}`}</span>;
      }
    },
    {
      field: 'rol',
      headerName: 'Rol',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.roleName}</span>;
      }
    },
    {
      field: 'enabled',
      headerName: 'Habilitada',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={`${params.row.enabled  ? 'success' : 'error'}`}
                     label={`${params.row.enabled  ? 'SI' : 'NO'}`}
                     sx={{width: 70, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create/user-account');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id+'/user-account')
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handleChangeRole = (event: SelectChangeEvent) => {
    setRoleFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchUsers(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchUsers(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const fetchUsers = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);
    type Param = {[key: string] : string}
    const params: Param = {}
    params.page = String(page);
    params.size = String(size);
    params.sort = sort;
    params.order = order;

    if (roleFilter !== '-1') {
      params.roleId = roleFilter;
    }

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    getUsers(params).then( (response) => {
      setUsersPage(response.data)
      setLoading(false);
    })
  };

  const fetchRoles = async () => {
    getRoles({page: 0, size: 100, sort: "id", order: "asc"}).then( (response) => {
      setRoles(response.data.content)
    })
  };
  
  useEffect(() => {
    fetchRoles()
    fetchUsers();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <ManageAccountsIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Usuarios y Legajos
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={6} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Nombre de Usuario y/o Email"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchUsers();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={3} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="role-label" shrink>Rol</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    label="Rol"
                    value={roleFilter}
                    onChange={handleChangeRole}
                  >
                    <MenuItem key="ALL" value="-1">Todos</MenuItem>
                    {roles?.map((role) => (
                      <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>
            <Grid xl={3} md={6} sm={6} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchUsers()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 632, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                rows={usersPage.content}
                columns={columns}
                columnVisibilityModel={{
                  id: false,
                }}
                paginationModel={paginationModel}
                rowCount={usersPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserList;
